<template>
  <div class="relative">
    <figure
      class="relative h-[400px] w-full overflow-hidden tablet:h-[430px] desktop:h-[520px]"
    >
      <picture>
        <source
          :srcset="bannerData.webBackgroundImage"
          type="image/webp"
          media="(min-width: 1025px)"
          width="1920"
          height="520"
        />
        <source
          :srcset="bannerData.tabletBackgroundImage"
          type="image/webp"
          media="(min-width: 768px)"
          width="1024"
          height="430"
        />
        <source
          :srcset="bannerData.mobileBackgroundImage"
          type="image/webp"
          media="(max-width: 767px)"
          width="480"
          height="400"
        />
        <img
          :src="bannerData.webBackgroundImage"
          :alt="bannerData.title"
          :loading="slideIndex > 0 ? 'lazy' : 'eager'"
          :fetchpriority="slideIndex === 0 ? 'high' : 'low'"
          width="1920"
          height="520"
          class="pointer-events-none block h-full w-full object-cover"
        />
      </picture>
    </figure>
    <div
      class="absolute left-0 top-0 flex h-[calc(400px-68px)] w-full tablet:h-[calc(430px-68px)] desktop:h-[calc(520px-68px)]"
    >
      <div class="container-wrap">
        <div class="w-full max-w-[760px]">
          <strong
            class="element-banner-title"
            :class="bannerData.textColor === 0 ? 'text-black' : 'text-white'"
            v-text="bannerData.title"
          />
          <p
            class="hidden tablet:line-clamp-2 tablet:max-h-12 tablet:whitespace-pre-line tablet:text-base desktop:max-h-14 desktop:text-xl"
            :class="bannerData.textColor === 0 ? 'text-gray-600' : 'text-white'"
            v-text="bannerData.description"
          />
        </div>
        <div class="mt-8 flex flex-col gap-3 tablet:flex-row tablet:gap-4">
          <TheButton
            v-if="bannerData.bannerLinks[0]"
            :id="isCN && slideIndex === 0 ? 'tracking_dialogue' : undefined"
            :to="provideFormatUrl(bannerData.bannerLinks[0].url)"
            :title="bannerData.bannerLinks[0].name"
            class="w-full tablet:w-[180px]"
            @click="handleClick(bannerData.bannerLinks[0].name)"
          >
            <span class="text-sm">{{ bannerData.bannerLinks[0].name }}</span>
          </TheButton>
          <TheButton
            v-if="bannerData.bannerLinks[1]"
            :to="provideFormatUrl(bannerData.bannerLinks[1].url)"
            :title="bannerData.bannerLinks[1].name"
            type="outline"
            class="w-full bg-white/50 tablet:w-[180px]"
            @click="handleClick(bannerData.bannerLinks[1].name)"
          >
            <span class="text-sm">{{ bannerData.bannerLinks[1].name }}</span>
          </TheButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IBanner } from '@/assets/types/api/Banners';

  interface IProps {
    slideIndex: number;
    bannerData: IBanner;
  }

  withDefaults(defineProps<IProps>(), {
    slideIndex: 0,
    bannerData: () => {
      return {
        title: '',
        thumbnail: '',
        webBackgroundImage: '',
        tabletBackgroundImage: '',
        mobileBackgroundImage: '',
        textColor: 0,
        bannerLinks: [],
      };
    },
  });

  const { isCN } = useLanguage();
  const { provideFormatUrl } = useFormatUrl();

  const handleClick = (name: string | null): void => {
    if (name) {
      window.gtag('event', 'site_click', {
        Item: 'Banner_Home',
        Item_Name: name,
      });
    }
  };
</script>

<style lang="scss" scoped>
  .element-banner-title {
    @apply mb-2 line-clamp-3 block max-h-[6.75rem] whitespace-pre-line text-2xl leading-9 tablet:line-clamp-2 tablet:max-h-[4.75rem] tablet:text-[1.75rem] tablet:leading-[2.375rem] desktop:line-clamp-none desktop:max-h-none desktop:text-[2rem] desktop:leading-[2.75rem];
  }
</style>
